import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	OnDestroy,
	Output,
	ViewChildren,
	QueryList,
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormControl,
	Validators,
} from '@angular/forms';
import { Subscription as RxJsSubscription, Subject } from 'rxjs';
import {
	ErrorService,
	ExplainErrorsService,
	SessionService,
} from '@app/services';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Marker } from '../marker';
import { MarkerService } from '../marker.service';
import { LanguageTabComponent } from '@app/models/language';
import {
	tileLayer,
	latLng,
	Marker as sMarker,
	Icon,
	FeatureGroup,
	featureGroup,
	DrawEvents,
	LatLng,
	latLngBounds,
} from 'leaflet';

@Component({
	selector: 'hpf-marker-form',
	templateUrl: './marker-form.component.html',
})
export class MarkerFormComponent implements OnInit, OnDestroy {
	/** The model subscription */
	private subscriptions: RxJsSubscription[] = [];
	/** The marker to inject in the form */
	@Input() marker: Marker;
	/** Called the save button is clicked for a new instance */
	@Output() create = new EventEmitter<Marker>();
	/** Called the save button is clicked for an existing instance */
	@Output() update = new EventEmitter<void>();
	/** Called the delete button is clicked */
	@Output() delete = new EventEmitter<void>();
	/** Set loading state of the form */
	@Input() loading = false;
	/** Enable the deletion button */
	@Input() deletable = true;
	/** Enabled quick form */
	@Input() quickFormEnabled = environment.plugins.quickForm.enabled;
	/** Denotes if deltion in progress */
	deleting = false;
	/** Show delete modal */
	deleteModal = false;
	/** The form group to use */
	form: FormGroup;
	/** Denotes if the form is pending */
	saving = false;
	/** Check permission of update **/
	readOnly = false;
	@ViewChildren(LanguageTabComponent) languageTabs: QueryList<
		LanguageTabComponent
	>;

	locationFormUpdate = new Subject();
	locationMapUpdate = new Subject();

	/** Delay show map to fixing loading map problem */
	showMap = false;

	icon = new Icon({
		iconSize: [25, 41],
		iconAnchor: [13, 41],
		iconUrl: 'assets/marker-icon.png',
		shadowUrl: 'assets/marker-shadow.png',
	});

	mapOptions = {
		layers: [
			tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				minZoom: 1,
				attribution: '...',
			}),
		],
		zoom: 5,
		center: new LatLng(46.879966, -121.726909),
		maxBounds: latLngBounds(new LatLng(90, -168), new LatLng(-90, 190)),
	};
	mapDrawnItems: FeatureGroup = featureGroup();
	mapDrawOptions = {
		position: 'topleft',
		draw: {
			marker: {
				icon: this.icon,
			},
			polyline: false,
			circle: false,
			rectangle: false,
			polygon: false,
			circlemarker: false,
		},
		edit: {
			featureGroup: this.mapDrawnItems,
			edit: false,
			remove: false,
		},
	};

	/** Constructor */
	constructor(
		private formBuilder: FormBuilder,
		private errorService: ErrorService,
		public explainErrorsService: ExplainErrorsService,
		private markerService: MarkerService,
		private sessionService: SessionService
	) {}
	/** Init */
	async ngOnInit() {
		this.readOnly = !(await this.sessionService.checkPermission(
			'marker',
			'update'
		));

		this.subscriptions.push(
			this.locationFormUpdate.pipe(debounceTime(1000)).subscribe(() => {
				this.updateModel();
				this.updateMap();
			})
		);

		setTimeout(() => (this.showMap = true), 500);

		// Init model
		if (!this.marker) this.marker = new Marker();

		// Create form
		this.form = this.formBuilder.group({
			label: new FormControl(this.marker.props.label, [
				Validators.required,
			]),
			image: new FormControl(this.marker.props.image, [
				Validators.required,
			]),
			point_of_interests: new FormControl(
				this.marker.props.point_of_interests,
				[]
			),
			plateau: new FormControl(this.marker.props.plateau, [
				Validators.required,
			]),
			latitude: new FormControl(this.marker.props.latitude, [
				Validators.min(-90),
				Validators.max(90),
				Validators.required,
			]),
			longitude: new FormControl(this.marker.props.longitude, [
				Validators.min(-180),
				Validators.max(180),
				Validators.required,
			]),
		});
		// Update form when model loads or changes
		this.subscriptions.push(
			this.marker.subscribe(() => {
				this.updateForm();
			})
		);
		// Init map location
		setTimeout(() => this.updateMap(), 500);
	}
	/** Destroy */
	ngOnDestroy() {
		this.subscriptions.map((s) => s.unsubscribe());
	}
	/** Called on form submit */
	async onSubmit(): Promise<void> {
		// Saving flag
		this.saving = true;
		try {
			// Update model
			this.updateModel();
			for (let element of this.languageTabs) {
				await element.submit();
			}
			// Creation or update ?
			if (this.marker.isNew()) {
				// Creation
				const marker: Marker = await this.markerService.create(
					this.marker.toPayload()
				);
				this.create.next(marker);
			} else {
				// Update
				await this.markerService.update(
					this.marker.getId(),
					this.marker.toPayload()
				);
				this.update.next();
			}
		} catch (error) {
			this.errorService.handle(error);
		}
		// Saving flag
		this.saving = false;
	}
	/** Update models properties from inputs values */
	private updateModel(): void {
		for (const key of Object.keys(this.form.controls)) {
			this.marker.props[key] = this.form.get(key).value;
		}
	}
	/** Update inputs values from models properties */
	private updateForm(): void {
		this.form.setValue({
			label: this.marker.props.label,
			image: this.marker.props.image,
			point_of_interests: this.marker.props.point_of_interests,
			plateau: this.marker.props.plateau,
			latitude: this.marker.props.latitude,
			longitude: this.marker.props.longitude,
		});
	}
	/** Called on deletion */
	onDelete(): void {
		this.deleting = true;
		this.markerService
			.remove(this.marker.getId())
			.then(() => {
				this.delete.next();
			})
			.catch((error) => this.errorService.handle(error))
			.then(() => (this.deleting = false));
	}

	addMarker(e: DrawEvents.Created) {
		this.mapDrawnItems.clearLayers();
		this.mapDrawnItems.addLayer(e.layer);
		const latLng = (e.layer as sMarker).getLatLng();
		this.setPoiLocation(latLng.lat, latLng.lng);
	}

	setPoiLocation(lat: number | undefined, lng: number | undefined) {
		this.marker.props.latitude = lat;
		this.marker.props.longitude = lng;
		this.form.get('latitude').setValue(lat);
		this.form.get('longitude').setValue(lng);
	}

	setPoiMarker(lat: number, lng: number) {
		const marker = new sMarker(new LatLng(lat, lng), { icon: this.icon });
		this.mapDrawnItems.addLayer(marker);
	}

	updateMapCenter(lat: number, lng: number) {
		this.mapOptions.center = latLng(lat, lng);
	}

	updateMap() {
		const { latitude, longitude } = this.marker.props;
		if (latitude && longitude) {
			this.mapDrawnItems.clearLayers();
			this.setPoiMarker(latitude, longitude);
			this.updateMapCenter(latitude, longitude);
		}
	}
}
